// @flow
/* eslint react/jsx-no-bind:0 */
/* eslint react/forbid-prop-types:0 */

import * as React from 'react';
import buildTitle from 'app/common/react/components/growth/unauth/lib/buildTitle';
import Image from 'app/common/react/components/growth/unauth/Image/Image';
import PinHoverSocialText from 'app/common/react/components/growth/unauth/pin/PinImage/PinHoverSocialText';
import PinImageDomainLink from 'app/common/react/components/growth/unauth/pin/PinImage/PinImageDomainLink';
import PinImageHover from 'app/common/react/components/growth/unauth/pin/PinImage/PinImageHover';
import PropTypes from 'prop-types';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import UnauthRankingLogging from 'app/common/react/components/growth/unauth/lib/UnauthRankingLogging';
import UnauthUsage from 'app/common/lib/UnauthUsage';
import VideoIndicatorIcon from 'app/common/react/components/video/VideoIndicatorIcon';
import { compose } from 'redux';
import { getPinImage } from 'app/common/react/components/growth/unauth/pin/pinUtils';
import { getSrcSet } from 'app/common/react/components/growth/unauth/lib/ImageUtils';
import { smartTruncate } from 'app/common/lib/react/strUtils';
import { track } from 'app/common/lib/react/analytics';
import hoverable, { type hoverHandlersType } from 'app/common/lib/react/hoverable';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  type PinType,
  type SrcSetType,
} from 'app/common/react/components/growth/unauth/lib/PropTypes';
import { Box, Letterbox, Mask } from 'gestalt';

type OwnProps = {|
  clientJsActive?: boolean,
  desktopUnauthRankingLoggingData?: {|
    id: string,
  |},
  disableHoverText?: boolean,
  hideSocialText?: boolean,
  hideTitle?: boolean,
  hideClickthroughLink?: boolean,
  hybridClickthroughHandler?: () => void,
  hoverHandlers: hoverHandlersType,
  hoverTextOverride?: string,
  imageSize?: string,
  isHovering: boolean,
  maxPinImageHeight?: number,
  numServerRenderedPins?: number,
  onLoad?: () => void,
  pin: PinType,
  link?: string,
  pinImageWidthMax: number,
  pinLinkClickHandler: (e: Event) => void,
  pinTitle?: React.Node,
  showHover?: boolean,
  slotIndex?: number,
  srcSet?: SrcSetType,
  setHeight?: number,
  setWidth?: number,
  alignTopExpEnabled?: boolean,
  imageClickHandler: (e: Event) => void,
  viewParameter?: number,
  viewButtonStyleOverrides?: ?{
    fontSize?: string,
  },
|};

type Props = {|
  ...OwnProps,
  seoUnauthExperiments: UnauthSeoExperiments,
  isMainPin?: boolean,
|};

const DESKTOP_GIRAFFE_IMAGE_CLIP_HEIGHT = 600;

const styles = {
  brioContainer: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  link: {
    cursor: 'pointer',
    borderRadius: '6px',
    display: 'block',
    padding: '0px',
    fontWeight: 'bold',
    textDecoration: 'none',
    color: '#717171',
    overflow: 'hidden',
  },
  img: {
    WebkitTransition: 'opacity 0.04s linear',
    transition: 'opacity 0.04s linear',
    borderRadius: 'inherit',
    opacity: 1,
    display: 'block',
    margin: '0 auto',
    border: '0px',
    width: '100%',
    maxWidth: '100%',
    verticalAlign: 'middle',
    position: 'relative',
  },
  imageDim: {
    cursor: 'pointer',
    bottom: '0px',
    left: '0px',
    right: '0px',
    top: '0px',
    position: 'absolute',
    pointerEvents: 'none',
  },
  dimOverlayDark: {
    opacity: 0.55,
    bottom: '0px',
    left: '0px',
    right: '0px',
    top: '0px',
    backgroundColor: '#000',
    borderRadius: '6px 6px 0 0',
    position: 'absolute',
    WebkitTransition: 'opacity .04s linear',
    transition: 'opacity .04s linear',
  },
  dimGradient: {
    bottom: '0px',
    height: '100px',
    left: '0px',
    position: 'absolute',
    right: '0px',
  },
  placeholderText: {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bottom: '0px',
    color: '#f7f7f7',
    fontSize: '12px',
    height: '56px',
    left: '0px',
    lineHeight: '150%',
    marginTop: '-6px',
    opacity: '0.5',
    padding: '0 14px',
    position: 'absolute',
    right: '0px',
    textAlign: 'center',
    textShadow: '0 -1px rgba(0,0,0,0.17)',
    top: '45%',
    wordWrap: 'break-word',
  },
};

const renderImagePlaceholder = (text, style, inPinGridOptGroup) => (
  <div className={inPinGridOptGroup ? 'GrowthUnauthPinImage__Image' : ''} style={style}>
    <h4
      className="GrowthUnauthPinImage__placeholder"
      style={inPinGridOptGroup ? null : styles.placeholderText}
    >
      {text}
    </h4>
  </div>
);

class PinImage extends React.Component<Props> {
  static contextTypes = {
    country: PropTypes.string,
    isAuthenticated: PropTypes.bool.isRequired,
    isBot: PropTypes.bool.isRequired,
    logging: PropTypes.shape({
      viewParameter: PropTypes.number,
      viewType: PropTypes.number.isRequired,
    }),
    pageType: PropTypes.string.isRequired,
    unauthId: PropTypes.string.isRequired,
    userAgent: PropTypes.shape({
      canUseNativeApp: PropTypes.bool.isRequired,
      isTablet: PropTypes.bool.isRequired,
      platform: PropTypes.string.isRequired,
      platformVersion: PropTypes.arrayOf(PropTypes.number),
      raw: PropTypes.string.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    imageSize: '236x',
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.isHovering && nextProps.isHovering) {
      this.trackScopedAction('hover');
    }
  }

  getFirstValidImageSize(pin, sizes) {
    for (const size of sizes) {
      if (size in pin.images) {
        return pin.images[size].url;
      }
    }
    return undefined;
  }

  trackScopedAction = (action) => {
    let { viewParameter } = this.contextLoggingParameters();
    viewParameter = viewParameter !== undefined ? viewParameter : 'unknown';
    trackRegisterAction(`unauth_pin_image.${action}.${viewParameter}`);
  };

  contextLoggingParameters = () => ({
    viewParameter:
      this.props.viewParameter !== undefined
        ? this.props.viewParameter
        : this.context.logging.viewParameter,
    viewType: this.context.logging.viewType,
  });

  trackClick(element) {
    const event = PCONST.thrift.EventType.CLICK;
    const { viewType, viewParameter } = this.contextLoggingParameters();
    const component = PCONST.thrift.ComponentType.FLOWED_PIN;
    const loggingData = this.props.desktopUnauthRankingLoggingData;
    UnauthRankingLogging(
      event,
      viewType,
      viewParameter,
      element,
      component,
      loggingData,
      this.props.pin,
    );
  }

  trackClickthrough(element) {
    this.trackClick(element);
    track(PCONST.thrift.EventType.PIN_CLICKTHROUGH, {
      component: PCONST.thrift.ComponentType.FLOWED_PIN,
      element,
      grid_index: this.props.slotIndex,
      objectId: this.props.pin.id,
      view: this.context.logging.viewType,
      viewParameter: this.context.logging.viewParameter,
      sanitized_url: this.props.pin.link,
    });
  }

  handleLinkClickthrough = (e) => {
    this.trackClickthrough(PCONST.thrift.ElementType.WEBSITE_BUTTON);
    if (this.props.hybridClickthroughHandler) {
      this.props.hybridClickthroughHandler();
    }
    if (this.props.pinLinkClickHandler) {
      this.props.pinLinkClickHandler(e);
    }
    e.stopPropagation();
  };

  handleImageClick = (e) => {
    UnauthUsage.logPinCTAClick(`/pin/${this.props.pin.id}/`); // log PinClick so it can be consumed during registration
    this.props.imageClickHandler(e);
  };

  renderImageDim = (inPinGridOptGroup) => {
    const inlineStyle = this.props.showHover ? { ...styles.imageDim, opacity: 1 } : styles.imageDim;
    const classnameStyle = this.props.showHover ? { opacity: 1 } : null;
    const style = inPinGridOptGroup ? classnameStyle : inlineStyle;
    return (
      <div className="GrowthUnauthPinImage__imageDim" style={style}>
        <div
          className="GrowthUnauthPinImage__dimOverlayDark"
          style={inPinGridOptGroup ? null : styles.dimOverlayDark}
        />
        <div className="GrowthUnauthPinImage__dimGradientDark" style={styles.dimGradient} />
      </div>
    );
  };

  renderImage = (description, imgStyle, srcUrl, srcSet, image, inPinGridOptGroup, isMainPin) => {
    const imageComponent = (
      <Image
        alt={description}
        lazyLoad={false}
        onLoad={this.props.onLoad}
        renderPlaceholder={renderImagePlaceholder.bind(
          this,
          description,
          imgStyle,
          inPinGridOptGroup,
        )}
        className={inPinGridOptGroup ? 'GrowthUnauthPinImage__Image' : ''}
        src={srcUrl}
        srcSet={srcSet}
        style={imgStyle}
        isMainPin={isMainPin}
      />
    );
    const { setHeight, setWidth, alignTopExpEnabled } = this.props;
    if (setHeight !== undefined && setWidth !== undefined) {
      const aspectRatio = image.width / image.height;
      if (alignTopExpEnabled) {
        const viewportAspectRatio = setWidth / setHeight;
        let contentHeight = 0;
        let contentWidth = 0;
        if (aspectRatio < viewportAspectRatio) {
          contentHeight = setWidth / aspectRatio;
          contentWidth = setWidth;
        } else {
          contentHeight = setHeight;
          contentWidth = setHeight * aspectRatio;
        }
        const offsetLeft = (contentWidth - setWidth) / -2;
        return (
          /* The component below replicates Letterbox but with customized margins, which is
          unattainable with the existing library */
          <Mask width={setWidth} height={setHeight}>
            <div style={{ marginLeft: offsetLeft }}>
              <Mask width={contentWidth} height={contentHeight}>
                {imageComponent}
              </Mask>
            </div>
          </Mask>
        );
      } else {
        return (
          <Letterbox contentAspectRatio={aspectRatio} height={setHeight} width={setWidth}>
            {imageComponent}
          </Letterbox>
        );
      }
    } else {
      return imageComponent;
    }
  };

  render() {
    const {
      imageSize,
      pin,
      link,
      isHovering,
      hideClickthroughLink,
      hideTitle = false,
      hideSocialText,
      hoverHandlers,
      numServerRenderedPins,
      clientJsActive,
      setHeight,
      hoverTextOverride,
      disableHoverText,
      viewButtonStyleOverrides,
      seoUnauthExperiments,
      pinTitle = null,
      isMainPin = false,
    } = this.props;

    const videos = pin.videos;

    const isVideo = !!videos && !!videos.video_list;
    const { duration = 0 } =
      (isVideo && videos.video_list[Object.keys(videos.video_list)[0]]) || {};

    const image = getPinImage(pin, imageSize);
    const imageHeight = (image.height * this.props.pinImageWidthMax) / image.width;
    let srcSet;
    if (this.props.srcSet) {
      srcSet = getSrcSet(pin, this.props.srcSet);
    }

    let pinGridOptGroup = '';
    const enablePinIcon = this.context.pageType === 'board' || !isMainPin;
    if (this.context.pageType === 'pin') {
      pinGridOptGroup =
        seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_pin_v3') || '';
    } else if (this.context.pageType === 'board') {
      pinGridOptGroup =
        seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_board_v3') || '';
    }

    const inPinGridOptGroup = pinGridOptGroup.startsWith('enabled');

    let linkStyle = inPinGridOptGroup
      ? { background: pin.dominant_color }
      : { ...styles.link, background: pin.dominant_color };
    const maxPinImageHeight = this.props.maxPinImageHeight || DESKTOP_GIRAFFE_IMAGE_CLIP_HEIGHT;
    if (imageHeight > maxPinImageHeight && !pin.fullImage) {
      linkStyle = { ...linkStyle, maxHeight: maxPinImageHeight - 100 + 'px' };
    }
    let description = pin.description;
    if (description) {
      description = smartTruncate(description, 496);
    }

    let imgStyle = {};
    let linkTarget = '_blank';
    if (inPinGridOptGroup) {
      imgStyle = setHeight ? {} : { height: imageHeight };
      linkTarget = '_self';
    } else {
      imgStyle = setHeight ? styles.img : { ...styles.img, height: imageHeight };
    }
    if (
      isMainPin &&
      (seoUnauthExperiments.v2GetGroup('remove_similar_pins_plp') || '').startsWith(
        'enabled_no_similar_pins_test_larger',
      )
    ) {
      imgStyle = { ...imgStyle, height: '540px', width: '360px' };
    }

    const title = buildTitle(description, pin.description_html, pin.pin_join);
    const srcUrl = image.url;

    const pinFigure = (
      <React.Fragment>
        {this.renderImage(title, imgStyle, srcUrl, srcSet, image, inPinGridOptGroup, isMainPin)}
        {(isHovering || this.props.showHover) && (
          <React.Fragment>
            {this.renderImageDim(inPinGridOptGroup)}
            <PinImageHover
              numServerRenderedPins={numServerRenderedPins}
              clientJsActive={clientJsActive}
              handleImageClick={this.handleImageClick}
              repinCount={pin.repin_count}
              textOverride={hoverTextOverride}
              disableHoverText={disableHoverText}
              viewButtonStyleOverrides={viewButtonStyleOverrides}
            />
          </React.Fragment>
        )}
      </React.Fragment>
    );

    return (
      <React.Fragment>
        <div
          {...hoverHandlers}
          className="GrowthUnauthPinImage"
          onContextMenu={(e) => {
            e.stopPropagation();
            trackRegisterAction('unauth.context_menu_open.pin_image');
          }}
          style={inPinGridOptGroup ? null : styles.brioContainer}
        >
          <a
            href={link || '/pin/' + pin.id + '/'}
            style={linkStyle}
            target={linkTarget}
            title={description}
          >
            {pinFigure}
          </a>
          {isHovering && (
            <PinImageDomainLink
              pin={pin}
              handleLinkClickthrough={this.handleLinkClickthrough}
              hideClickthroughLink={hideClickthroughLink}
            />
          )}
          {enablePinIcon && !isHovering && isVideo ? (
            <Box
              dangerouslySetInlineStyle={{ __style: styles }}
              padding={2}
              position="absolute"
              bottom
              left
              display="flex"
            >
              <VideoIndicatorIcon duration={duration} />
            </Box>
          ) : null}

          {!hideSocialText && isHovering && pin.repin_count >= 5 ? (
            <Box marginRight={2} marginLeft={2} marginTop={2} position="absolute" right top>
              <PinHoverSocialText repinCount={pin.repin_count} />
            </Box>
          ) : null}
        </div>
        {!hideTitle && pinTitle}
      </React.Fragment>
    );
  }
}

export default compose(
  hoverable,
  withUnauthSeoExperiments,
)(PinImage);
